import {
  Button,
  Header,
  SpaceBetween,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import useInventoryByProductVersionQuery from 'features/inventory/hooks/useInventoryByProductVersionQuery';
import useOpenReorderSuggestionsQuery from 'features/inventory/hooks/useOpenReorderSuggestionsQuery';
import ProductDisplay from 'features/products/components/ProductDisplay';
import useDebouncedValue from 'hooks/useDebouncedValue';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ScanTravelerInventoryModal from './components/ScanTravelerInventoryModal';

export default function InventoryPage() {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const debouncedFilterText = useDebouncedValue(filterText);

  const {
    CustomPagination,
    isFetchingNextPage,
    pageData,
    refetch,
  } = useInventoryByProductVersionQuery({
    filterText: debouncedFilterText,
  });
  const {
    pageData: openSuggestions = [],
    isFetchingNextPage: suggestionsLoading,
    CustomPagination: SuggestionsPagination,
  } = useOpenReorderSuggestionsQuery();

  const columnDefinitions = useMemo(() => [
    {
      id: 'product',
      header: 'Product',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <ProductDisplay
          productDocId={item.productDocId}
          fields={['sku', 'name']}
          link
        />
      ),
    },
    {
      id: 'version',
      header: 'Version',
      cell: (item) => item.productVersionId,
    },
    {
      id: 'onHand',
      header: 'On Hand',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <Button variant="inline-link" onClick={() => navigate(`/inventory/${item.productDocId}/${item.productVersionId}`)}>
          {item.onHand}
        </Button>
      ),
    },
    {
      id: 'incomingSupply',
      header: 'Incoming',
      cell: (item) => item.incomingSupply ?? 0,
    },
    {
      id: 'committedDemand',
      header: 'Committed',
      cell: (item) => item.committedDemand ?? 0,
    },
    {
      id: 'netPosition',
      header: 'Net Position',
      cell: (item) => item.netPosition ?? (
        (item.onHand ?? 0) + (item.incomingSupply ?? 0) - (item.committedDemand ?? 0)
      ),
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      cell: (item) => item.updatedAt?.toDate?.().toLocaleString() || '—',
    },

  ], [navigate]);

  const suggestionColumnDefinitions = useMemo(() => [
    {
      id: 'product',
      header: 'Product',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <ProductDisplay
          productDocId={item.productDocId}
          fields={['sku', 'name']}
          link
        />
      ),
    },
    {
      id: 'version',
      header: 'Version',
      cell: (item) => item.productVersionId,
    },
    {
      id: 'netPosition',
      header: 'Net Position',
      cell: (item) => item.snapshot?.netPosition ?? '-',
    },
    {
      id: 'triggerQuantity',
      header: 'Trigger',
      cell: (item) => item.triggerQuantity,
    },
    {
      id: 'targetLevel',
      header: 'Target',
      cell: (item) => item.targetLevel,
    },
    {
      id: 'suggestedQuantity',
      header: 'Suggested Qty',
      cell: (item) => item.suggestedQuantity,
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      cell: (item) => item.updatedAt?.toDate?.().toLocaleString() || '—',
    },
    {
      id: 'actions',
      header: 'Actions',
      minWidth: 175,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <Button
          onClick={() => navigate(`/orders/create?type=internal&reorderSuggestionId=${item.id}`)}
        >
          Create order
        </Button>
      ),
    },
  ], [navigate]);

  return (
    <>
      <SpaceBetween size="l">
        <Table
          header={<Header>Open Reorder Suggestions</Header>}
          stickyHeader
          resizableColumns
          wrapLines
          loading={suggestionsLoading}
          items={openSuggestions}
          columnDefinitions={suggestionColumnDefinitions}
          pagination={<SuggestionsPagination />}
          empty={<div>No open reorder suggestions</div>}
        />
        <Table
          header={(
            <Header
              variant="awsui-h1-sticky"
              actions={(
                <Button
                  onClick={() => setScanOpen(true)}
                >
                  Scan traveler
                </Button>
              )}
            >
              Inventory
            </Header>
          )}
          filter={(
            <TextFilter
              filteringPlaceholder="Find inventory by SKU"
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
            />
          )}
          variant="full-page"
          stickyHeader
          resizableColumns
          wrapLines
          loading={isFetchingNextPage}
          items={pageData}
          columnDefinitions={columnDefinitions}
          pagination={<CustomPagination />}
          empty={<div>No inventory found</div>}
        />
      </SpaceBetween>
      {scanOpen && (
        <ScanTravelerInventoryModal
          open
          onClose={() => setScanOpen(false)}
          onSuccess={refetch}
        />
      )}
    </>
  );
}
